/**
 * Client-side saved-payment-method model. Reads the user's safe card metadata reactively from Firestore
 * and carries the wallet mutations (add/update/delete + capture-session mint) as attached flow proxies.
 * The PCI Booking vault token is never present on this model — it lives in a server-only sibling document.
 */

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';

import { PaymentMethodModelSpec } from '@elara/domain/models/PaymentMethodModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';

export function createClientPaymentMethodModel(flows: AppFlowsContainer, firestore: FirestoreContainer) {
  const model = createClientModel(
    firestore,
    PaymentMethodModelSpec,
    () => ({
      /** Human label for the card, e.g. "Personal Visa" or "Visa ···· 1111". */
      get displayLabel(): string {
        return this.nickname || `${this.brand} ···· ${this.last4}`;
      },
      /** Whether the card's expiry month/year is in the past. */
      get isExpired(): boolean {
        const now = new Date();
        const endOfExpiryMonth = new Date(this.expYear, this.expMonth, 0, 23, 59, 59, 999);
        return endOfExpiryMonth.getTime() < now.getTime();
      },
      /**
       * Whether the card expires before `date` — used with a booking's check-out date (the latest the hotel
       * might charge it) so a card valid today but expiring mid-stay isn't offered.
       */
      expiresBefore(date: Date): boolean {
        const endOfExpiryMonth = new Date(this.expYear, this.expMonth, 0, 23, 59, 59, 999);
        return endOfExpiryMonth.getTime() < date.getTime();
      },
    }),
    {
      createCardCaptureSession: flows.createCardCaptureSession,
      addPaymentMethod: flows.addPaymentMethod,
      updatePaymentMethod: flows.updatePaymentMethod,
      deletePaymentMethod: flows.deletePaymentMethod,
    },
  );
  return model;
}

export type PaymentMethodModel = ReturnType<typeof createClientPaymentMethodModel>;
export interface PaymentMethod extends ClientInstanceOf<PaymentMethodModel> {}
